import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { isEditableTarget } from "@/lib/keyboard";
import { useLanguage } from "@/i18n/useLanguage";
import { LanguageBadge } from "@/i18n/LanguageBadge";
import { formatInTz } from "./dateUtils";
import { useEventDetail } from "./useEventDetail";

interface EventPanelProps {
  eventId: string;
  siblingIds: string[];
  tz: string;
  onSelect: (id: string) => void;
  /** Optional — accepted for back-compat with the prior Sheet-based panel.
   * The detail view is now always visible as a column, so closing it is a
   * no-op by default. */
  onClose?: () => void;
  /**
   * i18n Slice 2 — language the caller asked for. Optional so legacy
   * test callers without LanguageProvider keep rendering.
   */
  requestedLanguage?: string;
  /**
   * Language the calendar list endpoint actually returned (post-EN
   * fallback). Null until the list resolves; badge stays hidden.
   */
  servedLanguage?: string | null;
}

function importanceColor(imp: string): string {
  if (imp === "high") return "text-red-500";
  if (imp === "medium") return "text-amber-500";
  if (imp === "low") return "text-emerald-500";
  return "text-muted-foreground";
}

/**
 * Detail panel for a calendar event. Renders as a sibling column to the
 * agenda list (was previously an overlay Sheet). Internal layout uses
 * proportional vertical gap between sections rather than tight border-t
 * dividers so the content breathes.
 */
export function EventPanel({
  eventId,
  siblingIds,
  tz,
  onSelect,
  requestedLanguage,
  servedLanguage,
}: EventPanelProps) {
  const { data: ev, isLoading, error } = useEventDetail(eventId);
  const { rtl } = useLanguage();
  // C5: mirror prev/next chevrons so the arrow tip points toward the
  // direction of reading flow regardless of language.
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;
  const index = siblingIds.indexOf(eventId);
  const prevId = index > 0 ? siblingIds[index - 1] : null;
  const nextId =
    index >= 0 && index < siblingIds.length - 1 ? siblingIds[index + 1] : null;

  // Keyboard nav. Skip when an editable target has focus so typing into a
  // filter input doesn't also flip the displayed event.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      if (e.key === "ArrowLeft" && prevId) onSelect(prevId);
      if (e.key === "ArrowRight" && nextId) onSelect(nextId);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevId, nextId, onSelect]);

  return (
    <Card className="flex w-80 shrink-0 flex-col gap-4 overflow-hidden p-4 lg:w-[28rem]">
      <header className="flex flex-col gap-3 border-b border-border pb-3">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Previous event"
              disabled={!prevId}
              onClick={() => prevId && onSelect(prevId)}
            >
              <PrevIcon className="h-3 w-3" /> prev
            </Button>
            <span className="font-mono tabular-nums text-sky-400">
              {index >= 0 ? index + 1 : 0} / {siblingIds.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Next event"
              disabled={!nextId}
              onClick={() => nextId && onSelect(nextId)}
            >
              next <NextIcon className="h-3 w-3" />
            </Button>
          </div>
          <span className="text-muted-foreground">← →</span>
        </div>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold leading-tight">
            {ev?.title ?? "Loading…"}
          </h2>
          {/* i18n Slice 2 — "Available in English" indicator if the
              calendar list fell back from the user's requested language.
              The list-level served language is authoritative because all
              events in the panel come from the same fetch. */}
          {requestedLanguage && servedLanguage ? (
            <LanguageBadge
              requestedLanguage={requestedLanguage}
              servedLanguage={servedLanguage}
            />
          ) : null}
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto text-xs">
        {isLoading && (
          <div className="text-muted-foreground">Loading event…</div>
        )}
        {error && (
          <div className="text-red-400">Failed to load event.</div>
        )}

        {ev && (
          <>
            <section className="flex flex-col gap-1.5">
              <div className={cn("font-semibold", importanceColor(ev.importance))}>
                {ev.status === "scheduled" ? "FUTURE" : "OCCURRED"}
                {" · "}
                <span className="capitalize">{ev.importance}</span>
                {" · "}
                {ev.event_type}
                {" · "}
                {ev.country ?? "—"}
              </div>
              <div className="text-muted-foreground">
                {ev.scheduled_date}
                {ev.scheduled_at_utc && (
                  <>
                    {" · "}
                    {formatInTz(ev.scheduled_at_utc, tz, {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                    {" "}
                    <span className="opacity-70">
                      ({formatInTz(ev.scheduled_at_utc, "Etc/UTC", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })} UTC)
                    </span>
                  </>
                )}
              </div>
            </section>

            {(ev.forecast || ev.actual || ev.previous) && !ev.result_summary && (
              <section>
                <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Values
                </h3>
                <dl className="grid grid-cols-3 gap-2 rounded-md border border-border bg-muted/30 p-3">
                  {ev.actual ? (
                    <div className="flex flex-col">
                      <dt className="text-[10px] text-muted-foreground">Actual</dt>
                      <dd className="font-mono text-emerald-400">{ev.actual}</dd>
                    </div>
                  ) : (
                    <div />
                  )}
                  {ev.forecast ? (
                    <div className="flex flex-col">
                      <dt className="text-[10px] text-muted-foreground">Forecast</dt>
                      <dd className="font-mono text-foreground/90">{ev.forecast}</dd>
                    </div>
                  ) : (
                    <div />
                  )}
                  {ev.previous ? (
                    <div className="flex flex-col">
                      <dt className="text-[10px] text-muted-foreground">Previous</dt>
                      <dd className="font-mono text-foreground/90">{ev.previous}</dd>
                    </div>
                  ) : (
                    <div />
                  )}
                </dl>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Affects
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {ev.materials.length === 0 ? (
                  <span className="text-muted-foreground italic">No materials.</span>
                ) : (
                  ev.materials.map((m) => (
                    <span
                      key={m.material_id}
                      className="rounded-full bg-sky-500/10 px-2 py-0.5 text-sky-400"
                      title={m.reason}
                    >
                      <span>{m.material_id}</span>
                      {" · "}
                      {m.relevance_score.toFixed(2)}{" "}
                      <span className="text-sky-400/70">{m.impact_channel}</span>
                    </span>
                  ))
                )}
              </div>
            </section>

            {ev.result_summary && (
              <section>
                <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Result
                </h3>
                <p className="text-foreground/90">{ev.result_summary}</p>
                {ev.result_summary_source && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    source: {ev.result_summary_source}
                  </p>
                )}
              </section>
            )}

            {ev.description && (
              <section>
                <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Description
                </h3>
                <p className="whitespace-pre-line text-foreground/80">
                  {ev.description}
                </p>
                {ev.url && (
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-sky-400 hover:underline"
                  >
                    Open source ↗
                  </a>
                )}
              </section>
            )}

            <footer className="mt-auto border-t border-border pt-3 text-[10px] text-muted-foreground">
              source: {ev.source} · trust: {ev.source_trust}
              <br />
              {ev.id}
            </footer>
          </>
        )}
      </div>
    </Card>
  );
}
